import { ReactMediaRecorder } from "react-media-recorder";
import { useEffect, useMemo, useRef, useState } from "react";
import RecorderUIInner from "./recorder-ui-inner";
import {
  getSafeAudioConstraints,
  getSupportedMimeType,
} from "@/lib/mediaRecorder/utils";
import { RecorderStatus } from "@/common/types/recorder";
import { LoadingSpinner } from "../loading-spinner";
import { getAudioTranscriptionHook } from "@/hooks/transcriber/useAudioTranscriptionFactory";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { getAudioReactMediaRecorderOptions } from "@/hooks/transcriber/utils";

interface AudioRecorderUIProps {
  onFinished: (finished: boolean) => void;
  onRecordingComplete?: (blobUrl: string, transcription: string) => void;
}

const AudioRecorderUI: React.FC<AudioRecorderUIProps> = ({
  onFinished,
  onRecordingComplete,
}) => {
  const [autoStarted, setAutoStarted] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [finished, setFinished] = useState(false);
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null);
  const finalAudioStreamRef = useRef<MediaStream | null>(null);
  const { isRealtimeEnabled } = useRecordingSettingsStore.getState();

  const audioOptions = useMemo(() => {
    const options = getAudioReactMediaRecorderOptions(
      useRecordingSettingsStore.getState().noiseReduction,
      useRecordingSettingsStore.getState().noiseSuppression
    );
    return {
      deviceId: useRecordingSettingsStore
        .getState()
        .getMicDeviceIdForReactMediaRecorder(),
      ...getSafeAudioConstraints(options),
    };
  }, []);

  const {
    setStream,
    transcription,
    finished: transcriptionFinished,
    finishSession: finishAudioTranscriptionSession,
    pauseStream,
    isProviderReady,
  } = getAudioTranscriptionHook(isRealtimeEnabled)();

  useEffect(() => {
    if (mediaBlobUrl) {
      if (isRealtimeEnabled) {
        if (transcriptionFinished) {
          onRecordingComplete?.(mediaBlobUrl, transcription);
        }
      } else {
        if (finished) {
          onRecordingComplete?.(mediaBlobUrl, transcription);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, onFinished, transcriptionFinished, mediaBlobUrl]);

  useEffect(() => {
    return (): void => {
      if (finalAudioStreamRef.current) {
        finalAudioStreamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        finalAudioStreamRef.current = null;
      }
    };
  }, []);

  if (!isProviderReady) {
    return <LoadingSpinner size={8} />;
  }

  return (
    <ReactMediaRecorder
      stopStreamsOnStop
      askPermissionOnMount={false}
      audio={audioOptions}
      mediaRecorderOptions={{
        mimeType: getSupportedMimeType(),
        audioBitsPerSecond: 24000,
      }}
      render={({
        status,
        startRecording,
        stopRecording,
        pauseRecording,
        resumeRecording,
        mediaBlobUrl,
        previewStream,
        previewAudioStream,
      }) => {
        if (!finalAudioStreamRef.current && previewAudioStream) {
          finalAudioStreamRef.current = previewAudioStream;
        }
        if (previewAudioStream && status === RecorderStatus.recording) {
          setStream(previewAudioStream);
        }
        if (status === RecorderStatus.paused) {
          pauseStream();
        }
        if (status === RecorderStatus.stopped && mediaBlobUrl) {
          setMediaBlobUrl(mediaBlobUrl);
        }

        if (
          status === RecorderStatus.stopped &&
          finished &&
          !transcriptionFinished
        ) {
          return <LoadingSpinner size={8} />;
        }

        return (
          <RecorderUIInner
            autoStarted={autoStarted}
            finished={finished}
            isRealtimeEnabled={isRealtimeEnabled}
            mediaBlobUrl={mediaBlobUrl ?? null}
            mediaStream={finished ? previewStream : previewAudioStream}
            pauseRecording={pauseRecording}
            recordingTime={recordingTime}
            resumeRecording={resumeRecording}
            setAutoStarted={setAutoStarted}
            setFinished={setFinished}
            setRecordingTime={setRecordingTime}
            setShowConfirmation={setShowConfirmation}
            showConfirmation={showConfirmation}
            startRecording={startRecording}
            status={status}
            stopRecording={() => {
              finishAudioTranscriptionSession();
              stopRecording();
            }}
          />
        );
      }}
    />
  );
};

export default AudioRecorderUI;
